import React, { forwardRef, useState } from 'react'
import {
  IStyledInputBaseProps,
  IInputProps,
  IInputBlockProps,
  ITextareaProps,
  TPasswordProps,
  IChildrenProps,
} from './Input.types'
import { EyeHidden, Eye } from './Icons'
import css from './Input.module.scss'

export const InputBase = forwardRef<
  HTMLInputElement | HTMLTextAreaElement,
  IStyledInputBaseProps
>(({ className, as, ...passingProps }, ref) => {
  const Element = as === 'textarea' ? 'textarea' : 'input'
  return (
    <Element
      className={`${css.inputBase} ${className}`}
      // @ts-ignore
      ref={ref}
      {...passingProps}
      invalid={undefined}
    />
  )
})
export const Label = ({ children }: IChildrenProps) => (
  <p className={css.inputLabel}>{children}</p>
)

export const HelpText = ({ children }: IChildrenProps) => (
  <p className={css.helpText}>{children}</p>
)

export const Required = ({ children }: IChildrenProps) => (
  <span className={css.required}>{children}</span>
)

Required.defaultProps = {
  children: ' *',
}

export const Root = ({
  $isBlock,
  isDark,
  className,
  children,
}: IInputBlockProps) => (
  <div
    className={`${css.root} ${$isBlock ? css.isBlock : ''} ${
      isDark ? css.inputDark : ''
    } ${className}`}
  >
    {children}
  </div>
)

export const Input = forwardRef<HTMLInputElement, IInputProps>(
  (
    {
      className,
      helpText,
      iconLeft,
      iconRight,
      invalid,
      isBlock,
      isDark,
      label,
      prefix,
      required,
      suffix,
      ...passingProps
    },
    ref
  ) => (
    <Root isDark={isDark} $isBlock={isBlock}>
      {label && (
        <Label>
          {label}
          {required ? <Required /> : null}
        </Label>
      )}

      <div className={`${css.inputWrap} ${isBlock ? css.isBlock : ''}`}>
        {prefix && <div className={css.prefix}>{prefix}</div>}

        <div className={css.iconsWrap}>
          {iconLeft && (
            <div className={`${css.iconWrap} ${css.left}`}>{iconLeft}</div>
          )}

          <input type="hidden" ref={ref} />

          {/** @ts-ignore */}
          <InputBase
            {...passingProps}
            className={`${css.styledInput} ${isBlock ? css.isBlock : ''} ${
              invalid ? css.isInvalid : ''
            }
            ${iconLeft ? css.hasIconLeft : ''}
            ${iconRight ? css.hasIconRight : ''}
            ${suffix ? css.hasSuffix : ''}
            ${prefix ? css.hasPrefix : ''}
            ${className}`}
            ref={ref}
            required={required}
          />

          {iconRight && (
            <div className={`${css.iconWrap} ${css.right}`}>{iconRight}</div>
          )}
        </div>

        {suffix && <div className={css.suffix}>{suffix}</div>}
      </div>

      {helpText && <HelpText>{helpText}</HelpText>}
    </Root>
  )
)

export const Textarea = forwardRef<HTMLTextAreaElement, ITextareaProps>(
  (
    {
      className,
      invalid,
      isBlock,
      isDark,
      label,
      required,
      helpText,
      ...passingProps
    },
    ref
  ) => (
    <Root isDark={isDark} $isBlock={isBlock}>
      {label && (
        <Label>
          {label}
          {required ? <Required /> : null}
        </Label>
      )}

      <InputBase
        {...passingProps}
        className={`${css.styledInput} ${isBlock ? css.isBlock : ''} ${
          invalid ? css.isInvalid : ''
        } ${className}
        `}
        //@ts-ignore
        ref={ref}
        required={required}
        as="textarea"
      />

      {helpText && <HelpText>{helpText}</HelpText>}
    </Root>
  )
)

export const Password = forwardRef<HTMLInputElement, TPasswordProps>(
  (
    {
      className,
      helpText,
      isDark,
      invalid,
      isBlock,
      required,
      label,
      prefix,
      suffix,
      ...passingProps
    },
    ref
  ) => {
    const [shown, setShown] = useState(false)

    return (
      <Root isDark={isDark} $isBlock={isBlock}>
        {label && (
          <Label>
            {label}
            {required ? <Required /> : null}
          </Label>
        )}

        <div className={`${css.inputWrap} ${isBlock ? css.isBlock : ''}`}>
          {prefix && <div className={css.prefix}>{prefix}</div>}

          <div className={css.iconsWrap}>
            {/** @ts-ignore */}
            <InputBase
              {...passingProps}
              className={`${css.styledInput} ${isBlock ? css.isBlock : ''} ${
                invalid ? css.isInvalid : ''
              }
              ${suffix ? css.hasSuffix : ''}
              ${prefix ? css.hasPrefix : ''}
              ${className}`}
              ref={ref}
              required={required}
              type={shown ? 'input' : 'password'}
            />

            <div className={`${css.iconWrap} ${css.right}`}>
              <div
                className={css.eyeWrap}
                onClick={() => setShown((prev) => !prev)}
              >
                {shown ? <EyeHidden /> : <Eye />}
              </div>
            </div>
          </div>

          {suffix && <div className={css.suffix}>{suffix}</div>}
        </div>

        {helpText && <HelpText>{helpText}</HelpText>}
      </Root>
    )
  }
)
